'use client';

import type { components } from '@/apis/spec/contentType';
import Icon from '@/Icon';
import { clc } from '@/legacy/components/base/utils';
import Img from '@/legacy/components/img/FillImage';
import Nation from '@/legacy/components/info/Nation';
import Crown from '@/legacy/components/new/Crown';
import { roundToNumber, StickerMap, tournamentV2DTO } from '@/legacy/components/new/tournament';
import championStyles from '@/legacy/components/new/tournamentChampion.module.scss';
import { openAppDownModal } from '@/legacy/components/new/TournamentLive';
import styles from '@/legacy/components/new/tournamentOpen.module.scss';
import BulletSplitBodyM16 from '@/legacy/components/Text/BulletSplitBodyM16';
import Link from 'next/link';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useTransTournament, useTransCommon } from '@/@shared/hooks/useTranslations';
import { TOURNAMENT_DETAILS_PATHNAME } from '@/legacy/constants/pathname';
import useLocaleLink from '@/legacy/hooks/useLocaleLink';
import { YYYYMMDDHHmm } from '@/utilities/date';
import { nullCashReplace, nullCasualReplace } from '@/utilities/stateTool';

// Import domains API
import { useFetchV2TournamentSpecificStatusQuery } from '@/domains/content/api/v2_tournament/useV2_tournamentQueries';

type Props = {};

const Page = ({ item }: { item: components['schemas']['TournamentDetailResponseDto'] }) => {
	const locale = useLocaleLink();
	const t = useTransTournament('temp.reward');

	const { isDiamond, isPrivate, diaReward } = tournamentV2DTO(item, t);

	return (
		<div className={styles.card}>
			<Link className={styles.tournamentCard} href={TOURNAMENT_DETAILS_PATHNAME(locale) + '?id=' + item.tournament.id}>
				{isDiamond ? (
					<Img className={styles.poster} src={nullCasualReplace(item.tournament.posterImageUrl)} />
				) : (
					<Img className={styles.poster} src={nullCashReplace(item.tournament.posterImageUrl)} />
				)}
				<div className={styles.top}>
					<div className={clc(styles.private, isPrivate && styles.active)}>
						{isPrivate && <Icon color1={'#FDFDFE'} path="Action_Exclude_24_White" />}
					</div>

					{!isDiamond && <Crown className={styles.crown} />}

					<Nation
						className={styles.nation}
						nationCode={item.tournament.nationCode}
						nationFlagImageUrl={item.tournament.nationFlagImageUrl}
					/>
				</div>

				<Icon className={styles.sticker} path={StickerMap[item.tournament.status as keyof typeof StickerMap]} />
			</Link>
			<div className={clc(styles.prizeInfo, isDiamond ? styles.casual : styles.cash)}>
				{isDiamond ? (
					<Icon className={styles.prizeIcon} path="Payment_Image_Diamond_48" />
				) : (
					<Icon className={styles.prizeIcon} path="Payment_Image_Cash_48" />
				)}
				{isDiamond ? (
					<span className={styles.text}>
						{t('participantMultiplier')}
						{item.tournament.realParticipationFee}
					</span>
				) : (
					<span className={styles.text}>{diaReward}</span>
				)}
			</div>
			<div className={styles.prizeDetails}>
				<h3 className={styles.prizeTitle}>{item.tournament.title}</h3>
				<BulletSplitBodyM16
					prefix={'R ' + roundToNumber[item.tournament.roundType as keyof typeof roundToNumber]}
					suffix={YYYYMMDDHHmm(new Date(item.tournament.startSchedule))}
				/>
				{/* <span className={styles.dateInfo}>R 16 • 2024.06.28 / 00:00</span> */}
			</div>
		</div>
	);
};

const TournamentOpen = () => {
	const homeText = useTransTournament('temp.home');
	const modalText = useTransCommon('temp.modal');
	const { data, error, isLoading } = useFetchV2TournamentSpecificStatusQuery({
		query: {
			limit: 10,
			page: 1,
			status: 'OPEN',
		},
	});

	const tournaments = Array.isArray(data?.data) ? data.data : [];

	// 에러 상태 처리 - 조용히 숨김
	if (error) {
		return <></>;
	}

	// 빈 상태 처리
	if (!isLoading && tournaments.length > 0) {
		return (
			<div className={styles.tournamentSection}>
				<button
					className={styles.tournamentHeader}
					onClick={() => {
						openAppDownModal('OPEN', modalText);
					}}
				>
					<div className={styles.tournamentDetails}>
						<h2 className={championStyles.championTitle}>{homeText('openTournament')}</h2>
						<span className={championStyles.championDescription}>{homeText('availableTournament')}</span>
					</div>

					<Icon className={styles.right} color1={'#FDFDFE'} path="Right" />
				</button>
				<div className={styles.openSection}>
					<Swiper
						breakpoints={{
							200: {
								slidesPerView: 1.5,
							},
							400: {
								slidesPerView: 2,
							},
							500: {
								slidesPerView: 2.5,
							},

							784: {
								slidesPerView: 3,
							},
							1000: {
								slidesPerView: 3.5,
							},
							1216: {
								slidesPerView: 4,
							},
						}}
						className="openSwiper"
						modules={[Navigation]}
						navigation={{
							disabledClass: 'open-button-disabled',
							nextEl: '.open-button-next',
							prevEl: '.open-button-prev',
						}}
						slidesPerView={1.5}
						spaceBetween={16}
					>
						{tournaments.map((item, index) => {
							return (
								<SwiperSlide key={item.tournament.id}>
									<Page item={item} />
								</SwiperSlide>
							);
						})}
						<div className="open-button-prev">
							<Icon color1={'#1A1A20'} path="Left" />
						</div>
						<div className="open-button-next">
							<Icon color1={'#1A1A20'} path="Right" />
						</div>
					</Swiper>
				</div>
			</div>
		);
	}

	return <></>;
};

export default TournamentOpen;
